'use client'

// Animation
import { motion } from 'framer-motion'

// Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faQrcode, faArrowRight } from '@fortawesome/free-solid-svg-icons'

// design-studio
import {
  ButtonLG,
  Container,
  fadeInUp,
  H1,
  HeroNoteLink,
  Lead,
  MotionWords,
  PoweredBy,
  Section,
  staggerContainer,
} from 'design-studio'

// Utils
import { Join } from 'design-studio/lib'
import { NavigationData } from '@/site/data-links'

import { HeroHowSection } from './section-hero-how'

export function Hero() {
  return (
    <Section>
      <Container className="max-w-8xl relative pt-28 pb-30 md:pt-38">
        {/* Hero: single column; right column typewriter (VerifyDisplay "verify_this λ") hidden for now — re-enable by uncommenting <VerifyDisplay /> below and restoring lg:grid-cols-[auto_320px] lg:gap-16 */}
        <div className="relative z-10 mx-auto grid max-w-6xl grid-cols-1 items-start gap-12">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, amount: 0.3 }}
            className="flex max-w-3xl flex-col justify-center space-y-9"
          >
            <motion.div variants={fadeInUp}>
              <HeroNoteLink
                classNames="mb-10 md:mb-12 2xl:mb-18"
                href="https://arxiv.org/abs/2601.11840"
                title="Imandra CodeLogician New Paper"
              >
                Read our new paper
              </HeroNoteLink>
              <H1 className="mb-5 w-full opacity-95 mix-blend-color-dodge">
                <MotionWords>
                  Logical reasoning for AI coding agents
                </MotionWords>
              </H1>
              <Lead size="lg" tone="muted">
                CodeLogician makes AI coding agents structure their reasoning in
                logic and uses a dedicated reasoning engine to systematically
                analyze what the code can actually do. So you catch edge cases,
                surface hidden behaviors, and ship with confidence instead of
                guesswork.
              </Lead>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <div className="mt-6 flex flex-col gap-2 md:flex-row md:items-center md:gap-26">
                <ButtonLG
                  href="#start-now"
                  variant="none"
                  ariaLabel="Start now — go to getting started section"
                  classNames="max-w-max font-display bg-teal-700 text-white font-bold whitespace-nowrap hover:bg-teal-600"
                >
                  <FontAwesomeIcon icon={faQrcode} className="h-4 w-4" />
                  {NavigationData.signup.heroLabel}{' '}
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="h-4 w-4 -translate-x-1 opacity-0 transition-all duration-200 ease-out group-hover:translate-x-1 group-hover:opacity-100"
                  />
                </ButtonLG>
                <span className="hidden sm:block">
                  <PoweredBy />
                </span>
              </div>

              <div className="mt-16 flex flex-wrap items-center gap-2 md:mt-28">
                {[
                  { label: '7-day free trial', dot: 'bg-teal-400' },
                  { label: 'pip installable', dot: 'bg-blue-400' },
                  {
                    label: 'Works with Claude, GPT, Cursor, Gemini, Grok',
                    dot: 'bg-violet-400',
                  },
                ].map(({ label, dot }) => (
                  <span
                    key={label}
                    className="flex items-center gap-1.5 rounded-full border border-slate-700/50 bg-slate-800/50 px-3 py-1 font-mono text-xs text-slate-300 backdrop-blur-sm"
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
                    {label}
                  </span>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
        <HeroHowSection />
      </Container>
    </Section>
  )
}
